import { useState, useEffect, useCallback } from 'react';
import { Member } from '../types';
import { ApiError } from '../utils/error-handler';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import { extractErrorMessage, logError } from '../utils/error-handler';

// Helper function to map numeric status codes to text status
const mapStatusCodeToText = (status: string | number): string => {
  const statusCode = typeof status === 'string' ? status : status.toString();
  
  const statusMap: Record<string, string> = {
    '0': 'active',     // Active member
    '1': 'invited',    // Invitation sent
    '2': 'suspended',  // Suspended member
    '3': 'inactive',   // Inactive member
    'active': 'active',
    'invited': 'invited',
    'suspended': 'suspended',
    'inactive': 'inactive'
  };
  
  return statusMap[statusCode] || 'active'; // Default to active if unknown
};

// Define interface to match the API response structure
interface UserData {
  _id: string;
  name?: string;
  email?: string;
}

interface RoleData {
  _id: string;
  name?: string;
}

interface MemberResponseItem {
  _id: string;
  userId: string | UserData;
  roleId: string | RoleData;
  status: string;
  role?: string;
  createdAt: string;
  updatedAt: string;
  [key: string]: any; // Allow for other properties
}

export function useOrganizationMembers(organizationId: string) {
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  
  const fetchMembers = useCallback(async () => {
    if (!organizationId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await organizationService.listOrganizationMembers(organizationId);
      
      // Transform the API response to match the Member interface
      const processedMembers = (response as any[]).map(member => {
        // organizationService now properly maps the data
        const memberId = member.id || member._id;
        
        // Check for user data in nested object first (from service mapping)
        // then fall back to direct fields
        const userId = member.user_id || member.userId;
        const name = member.name || member.user?.name;
        const email = member.email || member.user?.email;
        const roleName = member.role_name || member.roleName || member.role;
        const status = member.status;
        const joinedAt = member.joined_at || member.joinedAt || member.createdAt;
        const updatedAt = member.updated_at || member.updatedAt || member.createdAt;
        
        const isRoleObject = typeof member.roleId === 'object' && member.roleId !== null;
        const roleData = isRoleObject ? member.roleId : { _id: member.roleId || member.role_id, name: roleName };
        
        return {
          id: memberId,
          userId: userId,
          name: name || 'Unknown',
          email: email || 'Unknown',
          roleId: roleData._id,
          role: roleName || (isRoleObject ? roleData.name : 'Unknown Role'),
          status: mapStatusCodeToText(status || '0'),
          createdAt: joinedAt || new Date().toISOString(),
          updatedAt: updatedAt || new Date().toISOString(),
        } as Member;
      });
      
      console.log('Processed members:', processedMembers);
      setMembers(processedMembers);
    } catch (err) {
      logError('Error fetching members', err);
      setError(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);
  
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);
  
  const inviteMember = async (email: string, roleId: string) => {
    try {
      setIsLoading(true);
      await invitationService.createInvitation(organizationId, { email, role: roleId });
      await fetchMembers(); // Refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };
  
  const updateMemberRole = async (memberId: string, role: string) => {
    try {
      setIsLoading(true);
      // Get the userId from the member that has the matching id
      const userId = members.find(m => m.id === memberId)?.userId;

      if (!userId) {
        throw new Error('Could not find user ID for this member');
      }

      await organizationService.updateOrganizationMemberRole(organizationId, userId, role);
      await fetchMembers(); // Refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      // Don't set persistent error state - let component handle with snackbar
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };
  
  const removeMember = async (memberId: string) => {
    try {
      setIsLoading(true);
      // Get the userId from the member that has the matching id
      const userId = members.find(m => m.id === memberId)?.userId;

      if (!userId) {
        throw new Error('Could not find user ID for this member');
      }

      await organizationService.removeOrganizationMember(organizationId, userId);
      await fetchMembers(); // Refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      // Don't set persistent error state - let component handle with snackbar
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    members,
    isLoading,
    error,
    refreshMembers: fetchMembers,
    inviteMember,
    updateMemberRole,
    removeMember
  };
}

export default useOrganizationMembers;